import { Icon } from '@blueprintjs/core'
import _ from 'lodash'
import React from 'react'

import { FramesManager } from 'shared-libs/components/view/frames-manager'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Footer } from 'browser/components/atomic-elements/atoms/footer/footer'
import { default as SheetPortal } from 'browser/components/atomic-elements/atoms/sheet/sheet-portal'

/**
 * @uiComponent
 *
 * A summary trigger that opens the caller's `children` form in a slide-in popup for editing a large
 * or nested value. Edits write through immediately ("Done" just closes). The collapsed trigger is the
 * caller's `summary` slot (any uiSchema node), falling back to a bare edit icon when omitted.
 *
 * Example:
 * {
 *   "type": "ui:formPopup",
 *   "title": "Entity Spec",
 *   "summary": {
 *     "type": "ui:view",
 *     "className": "c-entity-preview-card c-entity-preview-card--isClickable",
 *     "style": { "flexDirection": "row", "alignItems": "center", "justifyContent": "space-between" },
 *     "children": [
 *       { "type": "ui:view", "children": [
 *         { "type": "ui:view", "className": "c-entity-preview-card--title", "mapStateToProps": { "text": "mixins" } },
 *         { "type": "ui:view", "style": { "opacity": 0.6 }, "mapStateToProps": { "text": "ingestionFlags.writeBehavior" } }
 *       ] },
 *       { "type": "ui:icon", "name": "edit" }
 *     ]
 *   },
 *   "children": [ ... the value's form uiSchema ... ]
 * }
 */
export interface IFormPopupProps extends IBaseProps {
  frames?: FramesManager
  /** Bound value; drives the empty-state `placeholder`. */
  value?: any
  /** Popup header text (distinct from the collapsed summary). */
  title?: string
  /** Popup size passed through to the underlying Sheet (e.g. "sm", "md"). */
  size?: string
  /** Trigger text shown in place of `summary` for an unfilled entry (value undefined, null, or `{}`). */
  placeholder?: string
}

interface IFormPopupState {
  isOpen: boolean
}

export class FormPopup extends React.Component<IFormPopupProps, IFormPopupState> {
  public state: IFormPopupState = { isOpen: false }

  private open = () => this.setState({ isOpen: true })
  private close = () => this.setState({ isOpen: false })

  public render() {
    const { children, className, style, title, size } = this.props
    return (
      <div className={className} style={style}>
        {this.renderTrigger()}
        <SheetPortal isOpen={this.state.isOpen} size={size || 'sm'} footer={this.renderFooter()}>
          <div className="c-sheet-body--padded">
            {title && <h4 className="c-sheet-title u-bumperBottom">{title}</h4>}
            {children}
          </div>
        </SheetPortal>
      </div>
    )
  }

  private renderTrigger() {
    const { frames, placeholder, value } = this.props
    const summary = frames?.getContext('uiSchema')?.summary
    if (placeholder && (_.isNil(value) || (_.isObject(value) && _.isEmpty(value)))) {
      return (
        <div
          className="c-entity-preview-card c-entity-preview-card--isClickable"
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
          onClick={this.open}
        >
          <span className="c-placeholderColor">{placeholder}</span>
          <Icon icon="edit" />
        </div>
      )
    }
    if (summary && frames) {
      const renderer = frames.getContext('renderer')
      const uiSchemaPath = frames.getContext('uiSchemaPath').concat(['summary'])
      const summaryFrames = renderer.createChildFrame(frames, { uiSchema: summary, uiSchemaPath })
      return renderer.createElementFromFrame(summaryFrames, { onClick: this.open })
    }
    return <Icon icon="edit" onClick={this.open} />
  }

  private renderFooter() {
    // Only a "Done" button: edits write through live, so a "Cancel" that can't revert would mislead.
    return <Footer isVisible={true} primaryButtonText="Done" onPrimaryButtonClick={this.close} />
  }
}
